"use client";

import { format } from "date-fns";
import { srLatn } from "date-fns/locale";
import { ArrowRightIcon, CalendarClock, CheckCircle2 } from "lucide-react";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { parseAsIsoDateTime, parseAsStringEnum, useQueryState } from "nuqs";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button, buttonVariants } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CardDescription,
} from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { api } from "~/trpc/react";
import { pushGA4Event, setGA4UserData } from "~/lib/ga4-browser";
import { type RouterOutput } from "~/server/api/root";
import { TherapyRelationshipType } from "~/prisma/browser";
import { cn } from "~/lib/utils";
import { useCountdownFromNow } from "~/hooks/use-countdown";

interface StepIntroCallConfirmProps {
  therapist: NonNullable<RouterOutput["therapistRouter"]["getTherapist"]>;
}

export default function StepIntroCallConfirm({
  therapist,
}: StepIntroCallConfirmProps) {
  const session = useSession();
  const [sessionDate] = useQueryState("date", parseAsIsoDateTime);
  const [sourceSurface] = useQueryState(
    "sourceSurface",
    parseAsStringEnum(["therapist_profile", "free_intro_call_directory"]),
  );
  const [relationshipId, setRelationshipId] = useState<string | null>(null);

  const scheduleIntroCall =
    api.therapySessionRouter.scheduleIntroCall.useMutation({
      onSuccess: (createdSession) => {
        const email = session.data?.user?.email;
        if (email) setGA4UserData({ email });
        pushGA4Event("generate_lead", {
          lead_type: "intro_call",
          therapist_id: therapist.id,
          source_surface: sourceSurface ?? "therapist_profile",
        });
        setRelationshipId(createdSession.therapyRelationshipId);
      },
    });

  if (session.status === "loading") {
    return (
      <div className="w-full max-w-md space-y-4 p-6">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (session.status !== "authenticated") {
    return (
      <Alert variant="destructive" className="mx-auto max-w-md">
        <AlertDescription>
          Prijavi se da potvrdiš uvodni razgovor.
        </AlertDescription>
      </Alert>
    );
  }

  if (!sessionDate) {
    return (
      <Alert variant="destructive" className="mx-auto max-w-md">
        <AlertDescription>Izaberi termin pre potvrde.</AlertDescription>
      </Alert>
    );
  }

  if (relationshipId) {
    return (
      <IntroCallSuccess
        relationshipId={relationshipId}
        therapistName={therapist.user.name ?? ""}
        formattedDate={format(sessionDate, "EEEE, d. MMMM yyyy. 'u' HH:mm", {
          locale: srLatn,
        })}
      />
    );
  }

  const formattedDate = format(sessionDate, "EEEE, d. MMMM yyyy. 'u' HH:mm", {
    locale: srLatn,
  });
  const changeTimeHref = `/terapeuti/${therapist.slug}/book?step=0&therapyType=${TherapyRelationshipType.INDIVIDUAL}&sessionPurpose=INTRO_CALL${sourceSurface ? `&sourceSurface=${sourceSurface}` : ""}`;

  return (
    <div className="w-full max-w-lg space-y-5">
      <div className="space-y-2 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CalendarClock className="h-6 w-6" />
        </div>
        <h2 className="text-xl font-semibold">Potvrdi uvodni razgovor</h2>
        <p className="text-sm text-muted-foreground">
          {therapist.user.name}, {formattedDate}
        </p>
      </div>

      <div className="space-y-3 rounded-lg border bg-muted/30 p-4 text-sm">
        <p className="font-medium text-foreground">Detalji uvodnog razgovora</p>
        <dl className="space-y-3">
          <div className="grid gap-1 sm:grid-cols-[7rem_1fr]">
            <dt className="text-muted-foreground">Trajanje</dt>
            <dd className="font-medium text-foreground">15 minuta</dd>
          </div>
          <div className="grid gap-1 sm:grid-cols-[7rem_1fr]">
            <dt className="text-muted-foreground">Svrha</dt>
            <dd className="text-foreground">
              Upoznavanje sa terapeutom i osnovna pitanja
            </dd>
          </div>
          <div className="grid gap-1 sm:grid-cols-[7rem_1fr]">
            <dt className="text-muted-foreground">Važno</dt>
            <dd className="text-foreground">
              Ovo nije terapijska seansa, već prvi korak pre zakazivanja seanse.
            </dd>
          </div>
        </dl>
      </div>

      {scheduleIntroCall.error && (
        <Alert variant="destructive">
          <AlertDescription>
            {scheduleIntroCall.error.message || "Uvodni razgovor nije zakazan."}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Button
          type="button"
          className="w-full"
          isLoading={scheduleIntroCall.isPending}
          disabled={scheduleIntroCall.isPending}
          onClick={() =>
            scheduleIntroCall.mutate({
              therapistId: therapist.id,
              date: sessionDate,
              sourceSurface: sourceSurface ?? undefined,
            })
          }
        >
          Potvrdi uvodni razgovor
        </Button>
        <Link
          href={changeTimeHref}
          aria-disabled={scheduleIntroCall.isPending}
          className={buttonVariants({
            variant: "ghost",
            className: cn(
              "w-full",
              scheduleIntroCall.isPending && "pointer-events-none opacity-50",
            ),
          })}
        >
          Izmeni termin
        </Link>
      </div>
    </div>
  );
}

function IntroCallSuccess({
  relationshipId,
  therapistName,
  formattedDate,
}: {
  relationshipId: string;
  therapistName: string;
  formattedDate: string;
}) {
  const router = useRouter();
  const countdown = useCountdownFromNow(5);
  const inboxHref = `/app/inbox/${relationshipId}`;

  useEffect(() => {
    if (!countdown.isExpired) return;
    router.push(inboxHref);
  }, [countdown.isExpired, inboxHref, router]);

  return (
    <div className="flex w-full justify-center">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1">
          <div className="mb-4 flex justify-center">
            <CheckCircle2 className="h-16 w-16 text-primary" />
          </div>
          <CardTitle className="text-center text-2xl text-primary">
            Uvodni razgovor je zakazan
          </CardTitle>
          <CardDescription className="text-center">
            Potvrda je poslata na email
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-3 rounded-lg bg-primary/10 p-3">
            <CheckCircle2 className="h-6 w-6 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Terapeut</p>
              <p className="font-semibold">{therapistName}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 rounded-lg bg-primary/10 p-3">
            <CalendarClock className="h-6 w-6 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Termin</p>
              <p className="font-semibold">{formattedDate}</p>
            </div>
          </div>
          <p className="text-center text-sm text-muted-foreground">
            Razgovor traje 15 minuta i nije terapijska seansa.
          </p>
        </CardContent>
        <CardFooter className="flex flex-col">
          <Link
            href={inboxHref}
            className={buttonVariants({
              variant: "default",
              className: "w-full",
            })}
          >
            Otvori razgovor
            <ArrowRightIcon className="ml-2 h-4 w-4" />
          </Link>
          <p className="mt-2 text-muted-foreground">
            Preusmeravamo te za {Math.max(countdown.seconds, 0)} sekundi
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
